import { useEffect } from "react";
import { useHydrated } from "remix-utils/use-hydrated";
import { getYotpoConfig, YOTPO_WIDGET_LOADER_BASE_URL } from "~/lib/yotpo/config";

const elementExists = (element: HTMLScriptElement): boolean => {
  if (element.src) {
    // Check by src attribute
    return !!document.head.querySelector(`script[src="${element.src}"]`);
  }
  const scriptId = element.getAttribute('data-script-id');
  if (scriptId) {
    // Check by data-script-id attribute
    return !!document.head.querySelector(`script[data-script-id="${scriptId}"]`);
  }
  return false;
};

const safeAppendScript = (element: HTMLScriptElement): void => {
  if (!elementExists(element)) {
    document.head.appendChild(element);
  }
};

const getLocaleDomainExtension = (locale: string) => {
  if (locale === "en-gb") return "co.uk"
  if (locale === "en-ie") return "com"
  return locale.split("-")[1]
}

const ExternalScripts = ({
  gtmID,
  trengoKey,
  allowHotJar,
  locale,
  isKlaviyoEnabled,
  isYotpoEnabled,
  isTaggrsEnabled,
}: {
  gtmID?: string;
  trengoKey?: string;
  allowHotJar: boolean;
  locale?: string;
  isKlaviyoEnabled: boolean;
  isYotpoEnabled: boolean;
  isTaggrsEnabled: boolean;
}) => {
  const isHydrated = useHydrated();
  const ext = locale ? getLocaleDomainExtension(locale) : "nl";

  useEffect(() => {
    const gtmScriptSrc = isTaggrsEnabled ? `https://sst.wovar.${ext}/UJaMdnrqCB.js?tg=` : `https://www.googletagmanager.com/gtm.js?id=`;
    if (gtmID?.length && typeof document !== "undefined") {
      const gtmScript = document.createElement("script");
      gtmScript.setAttribute('data-script-id', 'gtm-script');
      gtmScript.innerHTML = `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                '${gtmScriptSrc}'+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${gtmID}');`;

      safeAppendScript(gtmScript);
    }
    if (isKlaviyoEnabled) {
      const klaviyoScript = document.createElement("script");
      const klaviyoObject = document.createElement("script");
      klaviyoScript.async = true;
      klaviyoScript.type = "text/javascript";
      klaviyoObject.defer = true;

      klaviyoScript.src =
        "https://static.klaviyo.com/onsite/js/Ymy2ea/klaviyo.js";
      klaviyoObject.setAttribute('data-script-id', 'klaviyo-object');
      klaviyoObject.innerHTML = `!function(){if(!window.klaviyo){window._klOnsite=window._klOnsite||[];try{window.klaviyo=new Proxy({},{get:function(n,i){return"push"===i?function(){var n;(n=window._klOnsite).push.apply(n,arguments)}:function(){for(var n=arguments.length,o=new Array(n),w=0;w<n;w++)o[w]=arguments[w];var t="function"==typeof o[o.length-1]?o.pop():void 0,e=new Promise((function(n){window._klOnsite.push([i].concat(o,[function(i){t&&t(i),n(i)}]))}));return e}}})}catch(n){window.klaviyo=window.klaviyo||[],window.klaviyo.push=function(){var n;(n=window._klOnsite).push.apply(n,arguments)}}}}();`;

      safeAppendScript(klaviyoScript);
      safeAppendScript(klaviyoObject);
    }

    const yotpoConfig = getYotpoConfig(locale);
    if (yotpoConfig && isYotpoEnabled) {
      const yotpoScript = document.createElement("script");
      yotpoScript.async = true;
      yotpoScript.src = `${YOTPO_WIDGET_LOADER_BASE_URL}/${yotpoConfig.appKey}`;
      safeAppendScript(yotpoScript);
    }
    if (trengoKey?.length) {
      const trengoScript = document.createElement("script");
      trengoScript.defer = true;
      trengoScript.setAttribute('data-script-id', 'trengo-script');
      trengoScript.innerHTML = `
            window.Trengo = window.Trengo || {};
            window.Trengo.key = '${trengoKey}';
            (function(d, script, t) {
                script = d.createElement('script');
                script.type = 'text/javascript';
                script.async = true;
                script.src = 'https://static.widget.trengo.eu/embed.js';
                d.getElementsByTagName('head')[0].appendChild(script);
            }(document));`;

      safeAppendScript(trengoScript);
    }
    if (allowHotJar) {
      const hotjarScript = document.createElement("script");
      hotjarScript.defer = true;
      hotjarScript.setAttribute('data-script-id', 'hotjar-script');
      hotjarScript.innerHTML =
        process.env.NODE_ENV !== "development" && locale === "nl-nl"
          ? `(function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:3559368,hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`
          : `
              (function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:3563241,hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`;

    safeAppendScript(hotjarScript);
    }
  }, [gtmID, trengoKey, allowHotJar, isYotpoEnabled, isKlaviyoEnabled, isTaggrsEnabled, locale, ext]);
  useEffect(() => {
    if (isHydrated) {
      window.klaviyo?.isIdentified();
    }
  }, [isHydrated]);
  
  const noscriptSrc = isTaggrsEnabled ? `https://sst.wovar.${ext}/UJaMdnrqCB.html?tg=${gtmID}` : `https://www.googletagmanager.com/ns.html?id=${gtmID}`;

  return !gtmID ? null : (
    <>
      {/* GOOGLE TAG MANAGER NOSCRIPT */}
      <noscript>
        <iframe
          src={noscriptSrc}
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        />
      </noscript>
    </>
  );
};

export default ExternalScripts;
